import React from "react";
import { motion } from "framer-motion";
import {
    CreditCard,
    FileText,
    BookOpen,
    Newspaper,
    Layers,
    Package,
    Tag,
    Sticker,
    Image,
    Flag,
    Lightbulb,
    Mail,
    Briefcase,
    Wand2,
    ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { buildWhatsAppLink } from "../lib/constants";

const PRODUCTS = [
    { id: "business-cards",  icon: CreditCard, name: "Business Cards",       desc: "Premium visiting cards — matte, gloss, textured and spot-UV finishes." },
    { id: "letterheads",     icon: FileText,   name: "Letterheads",          desc: "Clean, professional letterheads matched to your brand identity." },
    { id: "brochures",       icon: BookOpen,   name: "Brochures",            desc: "Bi-fold and tri-fold brochures that explain and sell your offering." },
    { id: "flyers",          icon: Newspaper,  name: "Flyers & Pamphlets",   desc: "High-impact flyers for offers, launches and local distribution." },
    { id: "catalogues",      icon: Layers,     name: "Catalogues",           desc: "Multi-page product catalogues designed for clarity and shelf appeal." },
    { id: "packaging",       icon: Package,    name: "Packaging & Boxes",    desc: "Cartons, pouches and rigid boxes — structure and artwork included." },
    { id: "labels",          icon: Tag,        name: "Labels",               desc: "Product labels compliant, legible and beautiful on any container." },
    { id: "stickers",        icon: Sticker,    name: "Stickers",             desc: "Die-cut brand stickers for packaging, laptops and giveaways." },
    { id: "posters",         icon: Image,      name: "Posters",              desc: "Large-format posters for retail, events and campaigns." },
    { id: "banners",         icon: Flag,       name: "Banners & Flex",       desc: "Outdoor and indoor flex banners printed for durability and colour." },
    { id: "signage",         icon: Lightbulb,  name: "Signage & Glow Boards", desc: "Glow sign boards, acrylic 3D letters and complete shop facades." },
    { id: "invitations",     icon: Mail,       name: "Invitations",          desc: "Wedding, corporate and event invitations — print and digital." },
    { id: "stationery",      icon: Briefcase,  name: "Corporate Stationery", desc: "Envelopes, notepads, folders, ID cards and full office kits." },
    { id: "custom",          icon: Wand2,      name: "Custom Print Products", desc: "Something specific in mind? We design and produce custom jobs." },
];

const waFor = (p) =>
    buildWhatsAppLink(`Hi Raj Graphics! I need a quote for ${p.name}. Please share pricing and options.`);

const PrintCataloguePage = () => {
    useDocumentMeta({
        title: "Printing Services in Nagpur — Business Cards, Brochures, Banners | Raj Graphics",
        description:
            "Design-led printing in Nagpur: business cards, letterheads, brochures, catalogues, packaging, labels, banners, signage and corporate stationery — designed and produced under one roof.",
        canonical: "/print",
    });

    return (
        <main data-testid="print-page" className="bg-[#050505]">
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Print Catalogue
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="print-headline"
                    >
                        Designed here. <span className="gold-gradient-text">Printed right.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        One studio for design and production — so nothing gets lost between the
                        designer and the printer. Browse what we produce, then ask for a quote on
                        WhatsApp.
                    </motion.p>
                </div>
            </section>

            <section className="relative py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div
                        data-testid="print-products-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                    >
                        {PRODUCTS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                                    data-testid={`print-product-${p.id}`}
                                    className="group flex flex-col bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-7 hover:border-gold/40 hover:bg-white/[0.04] transition-all duration-300"
                                >
                                    <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                                        <Icon size={18} className="text-gold" strokeWidth={1.7} />
                                    </div>
                                    <h2 className="font-poppins font-medium text-white text-lg">
                                        {p.name}
                                    </h2>
                                    <p className="mt-2 font-inter text-sm text-zinc-400 leading-relaxed flex-1">
                                        {p.desc}
                                    </p>
                                    <a
                                        href={waFor(p)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`print-cta-${p.id}`}
                                        className="mt-5 self-start inline-flex items-center gap-2 text-gold font-inter text-sm hover:underline underline-offset-4"
                                    >
                                        Get a quote
                                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                                    </a>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="mt-14 text-center">
                        <p className="font-inter text-zinc-400 mb-5">
                            Have artwork ready, or need design from scratch? Either works.
                        </p>
                        <Link
                            to="/get-quote"
                            data-testid="print-quote-cta"
                            className="inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-7 py-3.5 rounded-full transition-colors"
                        >
                            Request a detailed quote <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default PrintCataloguePage;
